import { useCallback } from 'react';
import useAnalytics from './use-analytics';

/**
 * Convenience wrapper hook for common Amplitude tracking patterns
 *
 * Usage:
 *   const { track, trackPage, trackClick, trackError } = useAnalyticsHelpers();
 *
 *   // Track custom event
 *   track('Purchase Completed', { amount: 99.99, currency: 'USD' });
 *
 *   // Track button click
 *   <button onClick={() => trackClick('Subscribe Button')}>Subscribe</button>
 *
 *   // Track page view (typically in useEffect)
 *   useEffect(() => {
 *     trackPage('Billing Page');
 *   }, []);
 *
 *   // Track errors
 *   try {
 *     // Some code
 *   } catch (error) {
 *     trackError(error, { component: 'BillingForm' });
 *   }
 */
const useAnalyticsHelpers = () => {
  const { track, trackError } = useAnalytics();

  const trackPage = useCallback(
    (pageName, properties = {}) => {
      track('Page View', {
        page: pageName,
        ...properties,
      });
    },
    [track]
  );

  const trackClick = useCallback(
    (elementName, properties = {}) => {
      track('Click', {
        element: elementName,
        ...properties,
      });
    },
    [track]
  );

  const trackButtonClick = useCallback(
    (buttonName, properties = {}) => {
      track('Button Clicked', {
        button: buttonName,
        ...properties,
      });
    },
    [track]
  );

  const trackLinkClick = useCallback(
    (linkName, properties = {}) => {
      track('Link Clicked', {
        link: linkName,
        ...properties,
      });
    },
    [track]
  );

  const trackNavigation = useCallback(
    (from, to, properties = {}) => {
      track('Navigation', {
        from,
        to,
        ...properties,
      });
    },
    [track]
  );

  return {
    track,
    trackError,
    trackPage,
    trackClick,
    trackButtonClick,
    trackLinkClick,
    trackNavigation,
  };
};

export default useAnalyticsHelpers;
